/**
 * General step 1 of a container: the campaign it runs on, picked among the
 * campaigns of its campaign group — the group open on the page (they share
 * one tracker link). A one-campaign group shows its campaign; a campaign
 * re-linked out of the group stays visible, flagged, with the way back.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { AlertTriangle, Megaphone } from 'lucide-react';

import { HintDot, SearchableSelect } from '@/components/ui';

import { middleTruncate } from '../../../campaign/campaign-scope';
import { FIELD_ICON, FIELD_LABEL } from '../../general/fields/field-chrome';
import type { ContainerEditor } from '../../state/useContainerEditor';
import { type EditorGroup, useEditorGroup } from './use-editor-group';

interface Props {
  editor: ContainerEditor;
  countryKey: string;
}

const HINT =
  'A campaign group is the SMSWiser campaigns that share one tracker link (Voluum or Binom), exactly like the SMSWiser page. Each container runs on ONE of them: its drafts carry that campaign. Switch group with the switcher beside the views.';

const ReadOnly: FC<{ name: string; wiserId: number }> = ({ name, wiserId }) => (
  <span className="flex h-10 min-w-0 items-center gap-2 rounded-md border border-border/60 bg-muted/30 px-3 text-sm">
    <span className="truncate font-medium">{name}</span>
    <span className="shrink-0 font-mono text-xs text-muted-foreground">{wiserId}</span>
    <span className="shrink-0 text-xs text-muted-foreground">· the group&apos;s only campaign</span>
  </span>
);

function campaignOptions(group: EditorGroup, current: number | null, outside: boolean) {
  const options = group.members.map((m) => ({ value: m.wiserId, label: `${m.name} · ${m.wiserId}` }));
  if (outside && current !== null) options.push({ value: current, label: `${group.ix.nameOf(current)} · ${current} · outside this group` });
  return options;
}

export const GroupCampaignPicker: FC<Props> = ({ editor, countryKey }) => {
  const { draft, setField } = editor;
  const group = useEditorGroup(editor, countryKey);
  const current = draft.campaignWiserId;
  const outside = group.loaded && current !== null && !group.members.some((m) => m.wiserId === current);
  const only = group.members.length === 1 && current === group.members[0].wiserId ? group.members[0] : null;
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className={FIELD_LABEL}>
        <Megaphone className={FIELD_ICON} /> Campaign
        <HintDot label={HINT} />
      </span>
      {group.key === null ? (
        <p className="text-xs text-warning">Open a campaign group from the switcher beside the views first — a container runs on one of its campaigns.</p>
      ) : only ? (
        <ReadOnly name={only.name} wiserId={only.wiserId} />
      ) : (
        <SearchableSelect
          options={campaignOptions(group, current, outside)}
          value={current ?? ''}
          onChange={(v) => setField('campaignWiserId', Number(v))}
          placeholder={`Pick one of ${group.members.length} campaigns…`}
          searchPlaceholder="Search a campaign or ID…"
        />
      )}
      {group.key !== null ? (
        <span className="truncate text-[11px] text-muted-foreground" title={group.fullLabel}>
          from {middleTruncate(group.label, 40)} ·{' '}
          {group.members.length === 1 ? 'alone on its tracker link' : `${group.members.length} campaigns share its tracker link`}
        </span>
      ) : null}
      {outside ? (
        <span className="flex items-start gap-1 text-[11px] text-warning">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          Its campaign ({group.ix.nameOf(current!)}) is not in this group — its tracker link changed, or it belongs to another group. Pick one of the group&apos;s campaigns.
        </span>
      ) : null}
    </div>
  );
};
